@if (help; as h) {
  <app-drawer [title]="'help.pageHelp' | translate" [width]="560" [hasFooter]="true" (close)="close()">
    <!-- hero -->
    <div class="nh-hero">
      <div class="nh-hero-top">
        <div class="nh-hero-ic"><app-icon name="LifeBuoy" [size]="20"></app-icon></div>
        <div class="flex1">
          <div class="nh-hero-title">{{ h.title }}</div>
          <div class="row gap-6 mt-4">
            <app-tag kind="accent">{{ sectionKey | translate }}</app-tag>
            <app-tag>{{ h.tag }}</app-tag>
          </div>
        </div>
      </div>
      <div class="nh-hero-line">{{ h.tagline }}</div>
    </div>

    <!-- focused tab — this section's own help, above the shared page help -->
    @if (tabHelp; as t) {
      <div class="nh-tab">
        <div class="nh-tab-head">
          <app-tag kind="accent">{{ 'help.thisTab' | translate }}</app-tag>
          <div class="nh-tab-title">{{ t.title }}</div>
        </div>
        <p class="nh-lead">{{ t.lead }}</p>
        @if (t.points?.length) {
          <div>
            @for (p of t.points ?? []; track p.title) {
              <div class="nh-point">
                <div class="nh-dot sw"></div>
                <div>
                  <div class="nh-pt-t">{{ p.title }}</div>
                  <div class="nh-pt-d">{{ p.desc }}</div>
                </div>
              </div>
            }
          </div>
        }
        @if (t.examples?.length) {
          <div class="col gap-10" style="margin-top:12px">
            @for (ex of t.examples ?? []; track ex.title) {
              <div class="nh-example">
                <div class="nh-ex-t">{{ ex.title }}</div>
                <div class="nh-ex-rows">
                  @for (r of ex.rows; track $index) {
                    <div class="nh-ex-row" [class.strong]="r.strong">
                      <span class="nh-ex-l">{{ r.label }}</span>
                      <span class="nh-ex-v" [style.color]="r.accent ? 'var(--' + r.accent + ')' : null">{{ r.value }}</span>
                    </div>
                  }
                </div>
                @if (ex.note) { <div class="nh-ex-note">{{ ex.note }}</div> }
              </div>
            }
          </div>
        }
      </div>
    }

    <!-- the business -->
    <div class="nh-sec">
      <div class="nh-sec-head">
        <div class="nh-sec-ic biz"><app-icon name="Building" [size]="15"></app-icon></div>
        <div>
          <div class="nh-sec-kicker">{{ 'help.business' | translate }}</div>
          <div class="nh-sec-title">{{ 'help.whyExists' | translate }}</div>
        </div>
      </div>
      <p class="nh-lead">{{ h.business.lead }}</p>
      <div>
        @for (p of h.business.points; track p.title) {
          <div class="nh-point">
            <div class="nh-dot biz"></div>
            <div>
              <div class="nh-pt-t">{{ p.title }}</div>
              <div class="nh-pt-d">{{ p.desc }}</div>
            </div>
          </div>
        }
      </div>
    </div>

    <!-- the software -->
    <div class="nh-sec">
      <div class="nh-sec-head">
        <div class="nh-sec-ic sw"><app-icon name="Spark" [size]="15"></app-icon></div>
        <div>
          <div class="nh-sec-kicker">{{ 'help.software' | translate }}</div>
          <div class="nh-sec-title">{{ 'help.whatScreenDoes' | translate }}</div>
        </div>
      </div>
      <p class="nh-lead">{{ h.software.lead }}</p>
      <div>
        @for (p of h.software.points; track p.title) {
          <div class="nh-point">
            <div class="nh-dot sw"></div>
            <div>
              <div class="nh-pt-t">{{ p.title }}</div>
              <div class="nh-pt-d">{{ p.desc }}</div>
            </div>
          </div>
        }
      </div>
    </div>

    <!-- status lifecycle — per-product chip-flow diagrams (English-only guidance) -->
    @if (h.lifecycles; as lc) {
      <div class="nh-sec">
        <div class="nh-sec-head">
          <div class="nh-sec-ic biz"><app-icon name="Flag" [size]="15"></app-icon></div>
          <div>
            <div class="nh-sec-kicker">{{ lc.kicker || 'Lifecycle' }}</div>
            <div class="nh-sec-title">{{ lc.title || 'Status by product' }}</div>
          </div>
        </div>
        <p class="nh-lead">{{ lc.lead }}</p>
        @if (lc.legend?.length) {
          <div class="nh-legend">
            @for (g of lc.legend ?? []; track g.label) {
              <span class="nh-legend-item"><span class="nh-chip-dot" [attr.data-kind]="g.kind"></span>{{ g.label }}</span>
            }
          </div>
        }
        <div class="col gap-14" style="margin-top:14px">
          @for (life of lc.items; track life.product) {
            <div class="nh-life">
              <div class="nh-life-head">
                <div class="nh-life-title">{{ life.product }}</div>
                @if (life.success) { <app-tag kind="accent">{{ lc.successPrefix || 'Success' }} · {{ life.success }}</app-tag> }
              </div>
              <div class="nh-pt-d" style="margin:2px 0 10px">{{ life.lead }}</div>
              <div class="nh-flowline">
                @for (n of life.path; track $index) {
                  @if ($index > 0) { <span class="nh-arrow">→</span> }
                  <span class="nh-chip" [attr.data-kind]="n.kind || 'neutral'">
                    <span class="nh-chip-t">{{ n.status }}</span>
                    @if (n.note) { <span class="nh-chip-note">{{ n.note }}</span> }
                  </span>
                }
              </div>
              @for (br of life.branches ?? []; track $index) {
                <div class="nh-branch">
                  <div class="nh-branch-from"><span class="nh-branch-arrow">↳</span> {{ lc.fromLabel || 'from' }} <b>{{ br.from }}</b>@if (br.label) { <span class="nh-branch-tag">{{ br.label }}</span> }</div>
                  <div class="nh-flowline">
                    @for (n of br.nodes; track $index) {
                      @if ($index > 0) { <span class="nh-arrow">→</span> }
                      <span class="nh-chip" [attr.data-kind]="n.kind || 'neutral'">
                        <span class="nh-chip-t">{{ n.status }}</span>
                        @if (n.note) { <span class="nh-chip-note">{{ n.note }}</span> }
                      </span>
                    }
                  </div>
                  @if (br.desc) { <div class="nh-branch-desc">{{ br.desc }}</div> }
                </div>
              }
            </div>
          }
        </div>
      </div>
    }

    <!-- flow to book — numbered end-to-end steps -->
    @if (h.flow; as f) {
      <div class="nh-sec">
        <div class="nh-sec-head">
          <div class="nh-sec-ic sw"><app-icon name="Spark" [size]="15"></app-icon></div>
          <div>
            <div class="nh-sec-kicker">{{ 'help.flow' | translate }}</div>
            <div class="nh-sec-title">{{ 'help.howYouBook' | translate }}</div>
          </div>
        </div>
        <p class="nh-lead">{{ f.lead }}</p>
        <ol class="nh-flow">
          @for (s of f.steps; track s.title) {
            <li>
              <div class="nh-pt-t">{{ s.title }}</div>
              <div class="nh-pt-d">{{ s.desc }}</div>
            </li>
          }
        </ol>
      </div>
    }

    <!-- modes / variants — definition list -->
    @if (h.modes; as m) {
      <div class="nh-sec">
        <div class="nh-sec-head">
          <div class="nh-sec-ic biz"><app-icon name="Building" [size]="15"></app-icon></div>
          <div>
            <div class="nh-sec-kicker">{{ 'help.modes' | translate }}</div>
            <div class="nh-sec-title">{{ 'help.whatEachVariant' | translate }}</div>
          </div>
        </div>
        <p class="nh-lead">{{ m.lead }}</p>
        <div class="col gap-10">
          @for (mode of m.modes; track mode.name) {
            <div class="nh-mode">
              <div class="row gap-8" style="align-items:center;flex-wrap:wrap">
                <app-tag kind="accent">{{ mode.name }}</app-tag>
                @if (mode.when) { <span class="text-tiny muted">{{ mode.when }}</span> }
              </div>
              <div class="nh-pt-d" style="margin-top:4px">{{ mode.desc }}</div>
            </div>
          }
        </div>
      </div>
    }

    <!-- worked numerical examples -->
    @if (h.examples?.length) {
      <div class="nh-sec">
        <div class="nh-sec-head">
          <div class="nh-sec-ic sw"><app-icon name="Receipt" [size]="15"></app-icon></div>
          <div>
            <div class="nh-sec-kicker">{{ 'help.workedExamples' | translate }}</div>
            <div class="nh-sec-title">{{ 'help.theNumbers' | translate }}</div>
          </div>
        </div>
        <div class="col gap-10">
          @for (ex of h.examples ?? []; track ex.title) {
            <div class="nh-example">
              <div class="nh-ex-t">{{ ex.title }}</div>
              <div class="nh-ex-rows">
                @for (r of ex.rows; track $index) {
                  <div class="nh-ex-row" [class.strong]="r.strong">
                    <span class="nh-ex-l">{{ r.label }}</span>
                    <span class="nh-ex-v" [style.color]="r.accent ? 'var(--' + r.accent + ')' : null">{{ r.value }}</span>
                  </div>
                }
              </div>
              @if (ex.note) { <div class="nh-ex-note">{{ ex.note }}</div> }
            </div>
          }
        </div>
      </div>
    }

    <!-- related -->
    @if (h.related.length) {
      <div>
        <div class="nh-sec-kicker" style="margin-bottom:10px">{{ 'help.relatedPages' | translate }}</div>
        <div class="nh-related">
          @for (r of h.related; track r) {
            <button type="button" class="nh-relbtn" (click)="go(r)">
              <app-icon name="ArrowUpRight" [size]="12"></app-icon> {{ label(r) }}
            </button>
          }
        </div>
      </div>
    }

    <div drawer-footer class="row" style="width:100%;align-items:center">
      <div class="row gap-6" style="color:var(--text-soft);font-size:12px;margin-right:auto">
        <app-icon name="LifeBuoy" [size]="13"></app-icon> {{ sectionKey | translate }} · {{ h.title }}
      </div>
      <button type="button" class="btn primary" (click)="close()">{{ 'help.gotIt' | translate }}</button>
    </div>
  </app-drawer>
}
